import { useMutation, useQueryClient } from "@tanstack/react-query";
import { generatorSingleEndpointQueries } from "../../../hooks/useGeneratorSingleEndpoint";
import { editGeneratorSingleEndpointRequest } from "../../../hooks/useGeneratorSingleEndpointMutation";
import { variantGeneratorsQueries } from "../../../hooks/useVariantGenerators";
import { getNestedValue } from "../../BuildingBlocks/Forms/utils/getNestedValue";
import {
  GENERATOR_FEATURES,
  GeneratorFeatureKeys
} from "../../ComponentListContainer/ComponentList/ComponentEditWizard/Data/utils/getGeneratorFeatures";

export function useGeneratorFeatureMutation(variantId: number) {
  const queryClient = useQueryClient();
  const variantGeneratorsQueryKey =
    variantGeneratorsQueries.detail(variantId).queryKey;

  const editGeneratorFeature = useMutation({
    mutationFn: editGeneratorSingleEndpointRequest,
    onMutate: async (generator) => {
      if (!generator.id) return;

      await queryClient.cancelQueries({ queryKey: variantGeneratorsQueryKey });

      const snapshot = queryClient.getQueryData(variantGeneratorsQueryKey);

      queryClient.setQueryData(
        variantGeneratorsQueryKey,
        snapshot?.map((g) => {
          if (g.id === generator.id) {
            const updatedFeatures = Object.values(GeneratorFeatureKeys).reduce(
              (acc, key) => {
                const overviewKey =
                  GENERATOR_FEATURES.find((feature) => feature.key === key)
                    ?.overviewKey ?? key;
                const value = getNestedValue(generator, key);
                if (value !== undefined) {
                  acc[overviewKey] = value;
                }
                return acc;
              },
              {} as Partial<Generator>
            );

            return { ...g, ...updatedFeatures };
          }
          return g;
        })
      );

      return { snapshot };
    },
    onError: (_error, generator, context) => {
      if (!generator?.id || !context?.snapshot) return;

      queryClient.setQueryData(variantGeneratorsQueryKey, context.snapshot);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: generatorSingleEndpointQueries.all().queryKey
      });
      queryClient.invalidateQueries({
        queryKey: variantGeneratorsQueries.all().queryKey
      });
    }
  });

  return {
    editGeneratorFeature
  };
}
